import { useCallback, useRef, useState } from 'react';
import { SkippableFragment } from '@funtech-int/kp-graphql-sdk/yango-movies-www';

import { useInstanceCallback } from '@/scenarios/legacy-player/hooks/useInstanceCallback';
import { useOnCurrentTimeChanged } from '@/scenarios/legacy-player/hooks/useOnCurrentTimeChanged';
import { useOnPlayingStateChanged } from '@/scenarios/legacy-player/hooks/useOnPlayingStateChanged';
import { PlayingState } from '@/scenarios/legacy-player/types/instance';

import { useSwitchToNextEpisodeWithAnalytics } from '../../../../hooks/skin/use-switch-to-next-episode-with-analytics';

import { useIsAutoSkipEnabled } from './use-is-auto-skip-enabled';

interface Params {
    contentId: string;
    currentFragment: SkippableFragment;
}

const AUTO_SKIP_DURATION = 4.5;
const AUTO_SKIP_ANIMATION_DURATION = 5;
const DELTA = 0.11;
const AUTO_SKIP_CHECK_DELAY = 1000;

interface UseAutoSkipActivatorProps {
    skip: () => void;
    skippableFragment: SkippableFragment;
    isAutoSkipDisabled: boolean;
    onCurrentTimeChangedExtra?: (currentTime: number) => void;
}

const useAutoSkipStartTime = ({ startTime, endTime }: SkippableFragment) => {
    const [autoSkipStartTime, setAutoSkipStartTime] = useState<number | null>(null);

    useOnCurrentTimeChanged(
        useInstanceCallback(
            (instance, currentTime: number) => {
                const playingState = instance?.playingState;
                const isInRange = currentTime >= startTime && currentTime <= endTime;

                if (
                    !playingState ||
                    playingState === PlayingState.pause ||
                    (autoSkipStartTime !== null && !isInRange)
                ) {
                    setAutoSkipStartTime(null);

                    return;
                }

                if (autoSkipStartTime === null && isInRange) {
                    setAutoSkipStartTime(currentTime);
                }
            },
            [startTime, endTime, autoSkipStartTime]
        ),
        AUTO_SKIP_CHECK_DELAY
    );

    useOnPlayingStateChanged(
        useCallback((playingState: PlayingState | undefined) => {
            if (!playingState || playingState === PlayingState.pause) {
                setAutoSkipStartTime(null);
            }
        }, [])
    );

    return autoSkipStartTime;
};

export const useAutoSkipActivator = ({
    skip,
    skippableFragment,
    isAutoSkipDisabled,
    onCurrentTimeChangedExtra,
}: UseAutoSkipActivatorProps) => {
    const autoSkipStartTime = useAutoSkipStartTime(skippableFragment);
    const isSkipHappened = useRef<boolean>(false);

    const skipIfNecessary = useCallback(
        (currentTime: number) => {
            if (autoSkipStartTime === null) {
                return;
            }

            const timeToAutoSkipEnd = Math.abs(
                currentTime - (autoSkipStartTime + AUTO_SKIP_DURATION)
            );

            if (timeToAutoSkipEnd <= DELTA && !isAutoSkipDisabled && !isSkipHappened.current) {
                isSkipHappened.current = true;
                skip();
            }
        },
        [autoSkipStartTime, isAutoSkipDisabled, skip]
    );

    useOnCurrentTimeChanged(
        useCallback(
            (currentTime: number) => {
                skipIfNecessary(currentTime);
                onCurrentTimeChangedExtra?.(currentTime);
            },
            [onCurrentTimeChangedExtra, skipIfNecessary]
        )
    );

    return !isAutoSkipDisabled && autoSkipStartTime !== null
        ? AUTO_SKIP_ANIMATION_DURATION
        : undefined;
};

export const useAutoSkip = ({ contentId, currentFragment }: Params) => {
    const [isCancelled, setIsCancelled] = useState<boolean>(false);
    const [dismissSkipEnabled, setDismissSkipEnabled] = useState(true);

    const { isAutoSkipEnabled, autoSkipHappened } = useIsAutoSkipEnabled({
        contentId,
    });

    const skip = useSwitchToNextEpisodeWithAnalytics(contentId);

    const skipActivate = useCallback(() => {
        autoSkipHappened();
        skip('NEXT_EPISODE_AUTO_SKIP', currentFragment.type ?? 'UNKNOWN');
    }, [autoSkipHappened, currentFragment.type, skip]);

    const pause = useInstanceCallback((instance) => instance?.pause());

    const onCurrentFragmentEnd = useCallback(
        (currentTime: number) => {
            if (currentTime > currentFragment.endTime - 1 && !isAutoSkipEnabled) {
                pause();
                setDismissSkipEnabled(false);
            }
        },
        [currentFragment.endTime, isAutoSkipEnabled, pause]
    );

    const animationActive = useAutoSkipActivator({
        skip: skipActivate,
        skippableFragment: currentFragment,
        isAutoSkipDisabled: !isAutoSkipEnabled || isCancelled,
        onCurrentTimeChangedExtra: onCurrentFragmentEnd,
    });

    const stopAutoSkip = useCallback(() => {
        setIsCancelled(true);
    }, []);

    return {
        duration: animationActive,
        stopAutoSkip,
        dismissSkipEnabled,
    };
};
